import { useState, useRef, useCallback } from "react";
import { Trash2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InjuryMarker {
  id: string;
  side: "front" | "back";
  x: number;
  y: number;
  region: string;
  injuryType: InjuryType;
  notes?: string;
}

export type InjuryType =
  | "abrasion"
  | "bruise"
  | "broken_bone"
  | "puncture"
  | "other";

const INJURY_TYPES: {
  key: InjuryType;
  label: string;
  letter: string;
  color: string;
  bgColor: string;
}[] = [
  {
    key: "abrasion",
    label: "Abrasion",
    letter: "A",
    color: "#dc2626",
    bgColor: "bg-red-600",
  },
  {
    key: "bruise",
    label: "Bruise",
    letter: "B",
    color: "#7c3aed",
    bgColor: "bg-violet-600",
  },
  {
    key: "broken_bone",
    label: "Broken Bone",
    letter: "R",
    color: "#ea580c",
    bgColor: "bg-orange-600",
  },
  {
    key: "puncture",
    label: "Puncture Wound",
    letter: "P",
    color: "#0284c7",
    bgColor: "bg-sky-600",
  },
  {
    key: "other",
    label: "Other",
    letter: "O",
    color: "#525252",
    bgColor: "bg-neutral-600",
  },
];

interface BodyMapProps {
  value: InjuryMarker[];
  onChange: (markers: InjuryMarker[]) => void;
  isInjured?: "yes" | "no" | "na";
  onIsInjuredChange?: (value: "yes" | "no" | "na") => void;
  conditionWorsened?: "yes" | "no" | "na";
  onConditionWorsenedChange?: (value: "yes" | "no" | "na") => void;
}

// ---------------------------------------------------------------------------
// Body region definitions (SVG paths for front and back views)
// Anatomically proportioned, viewBox: 0 0 300 520
// ---------------------------------------------------------------------------

interface BodyRegion {
  name: string;
  label: string;
  path: string;
}

// Shared helper — regions use the same geometry for front/back
function makeRegions(side: "front" | "back"): BodyRegion[] {
  const s = side === "front" ? "" : "-back";
  const headLabel = side === "front" ? "Head" : "Back of Head";
  const neckLabel = side === "front" ? "Neck" : "Back of Neck";
  const torsoUpper = side === "front" ? "Chest" : "Upper Back";
  const torsoLower = side === "front" ? "Abdomen" : "Lower Back";
  const hipLabel = side === "front" ? "Groin / Hip" : "Buttocks";
  const thighLabel = (lr: string) =>
    side === "front" ? `${lr} Thigh` : `${lr} Hamstring`;
  const shinLabel = (lr: string) =>
    side === "front" ? `${lr} Shin` : `${lr} Calf`;
  const footLabel = (lr: string) =>
    side === "front" ? `${lr} Foot` : `${lr} Heel`;

  return [
    // Head — ellipse-like
    { name: `head${s}`, label: headLabel, path: "M127,18 C127,5 137,-2 150,-2 C163,-2 173,5 173,18 L173,42 C173,56 163,64 150,64 C137,64 127,56 127,42 Z" },
    // Neck
    { name: `neck${s}`, label: neckLabel, path: "M141,64 L159,64 L161,78 L139,78 Z" },
    // Left shoulder
    { name: `l-shoulder${s}`, label: `Left Shoulder${s ? " (Back)" : ""}`, path: "M139,78 L113,82 C106,84 100,90 97,97 L112,104 L139,94 Z" },
    // Right shoulder
    { name: `r-shoulder${s}`, label: `Right Shoulder${s ? " (Back)" : ""}`, path: "M161,78 L187,82 C194,84 200,90 203,97 L188,104 L161,94 Z" },
    // Chest / Upper back
    { name: `torso-upper${s}`, label: torsoUpper, path: "M139,78 L161,78 L161,94 L167,94 L170,150 L130,150 L133,94 L139,94 Z" },
    // Abdomen / Lower back
    { name: `torso-lower${s}`, label: torsoLower, path: "M130,150 L170,150 L172,210 L128,210 Z" },
    // Left upper arm
    { name: `l-upper-arm${s}`, label: `Left Upper Arm${s ? " (Back)" : ""}`, path: "M97,97 L88,102 C84,115 80,135 78,158 L93,160 L112,104 Z" },
    // Right upper arm
    { name: `r-upper-arm${s}`, label: `Right Upper Arm${s ? " (Back)" : ""}`, path: "M203,97 L212,102 C216,115 220,135 222,158 L207,160 L188,104 Z" },
    // Left forearm
    { name: `l-forearm${s}`, label: `Left Forearm${s ? " (Back)" : ""}`, path: "M78,158 L93,160 L84,232 L68,228 Z" },
    // Right forearm
    { name: `r-forearm${s}`, label: `Right Forearm${s ? " (Back)" : ""}`, path: "M222,158 L207,160 L216,232 L232,228 Z" },
    // Left hand
    { name: `l-hand${s}`, label: `Left Hand${s ? " (Back)" : ""}`, path: "M68,228 L84,232 L80,262 C78,268 72,272 66,270 L58,266 C56,260 58,250 60,244 Z" },
    // Right hand
    { name: `r-hand${s}`, label: `Right Hand${s ? " (Back)" : ""}`, path: "M232,228 L216,232 L220,262 C222,268 228,272 234,270 L242,266 C244,260 242,250 240,244 Z" },
    // Hip / Groin / Buttocks
    { name: `hip${s}`, label: hipLabel, path: "M128,210 L172,210 L174,240 C174,248 163,256 150,258 C137,256 126,248 126,240 Z" },
    // Left thigh
    { name: `l-thigh${s}`, label: thighLabel("Left"), path: "M126,240 C130,252 136,258 150,258 L148,340 L122,338 Z" },
    // Right thigh
    { name: `r-thigh${s}`, label: thighLabel("Right"), path: "M174,240 C170,252 164,258 150,258 L152,340 L178,338 Z" },
    // Left knee
    { name: `l-knee${s}`, label: "Left Knee", path: "M122,338 L148,340 L146,374 L120,372 Z" },
    // Right knee
    { name: `r-knee${s}`, label: "Right Knee", path: "M178,338 L152,340 L154,374 L180,372 Z" },
    // Left shin / calf
    { name: `l-shin${s}`, label: shinLabel("Left"), path: "M120,372 L146,374 L142,462 L118,458 Z" },
    // Right shin / calf
    { name: `r-shin${s}`, label: shinLabel("Right"), path: "M180,372 L154,374 L158,462 L182,458 Z" },
    // Left foot
    { name: `l-foot${s}`, label: footLabel("Left"), path: "M118,458 L142,462 L142,480 C142,488 136,494 126,496 L110,496 C108,492 110,478 112,468 Z" },
    // Right foot
    { name: `r-foot${s}`, label: footLabel("Right"), path: "M182,458 L158,462 L158,480 C158,488 164,494 174,496 L190,496 C192,492 190,478 188,468 Z" },
  ];
}

const FRONT_REGIONS = makeRegions("front");
const BACK_REGIONS = makeRegions("back");

// ---------------------------------------------------------------------------
// Realistic human body outline — gender-neutral silhouette with curves
// viewBox 0 0 300 520
// ---------------------------------------------------------------------------

// Single continuous human silhouette — gender-neutral, natural proportions
// No elf ears, no disjointed parts. Clean medical-diagram style.
const BODY_SILHOUETTE =
  // Top of head
  "M150,8" +
  // Right side of head (round cranium)
  " C168,8 178,20 178,38" +
  // Right ear (small, subtle bump)
  " C178,42 180,44 181,46 C182,49 182,52 181,54 C180,56 178,56 178,54" +
  // Right jaw down to neck
  " C178,58 172,66 166,70 C162,72 158,74 155,75" +
  // Right side of neck
  " L158,78 C158,82 160,86 164,88" +
  // Right shoulder
  " C172,92 184,96 194,102 C200,106 206,112 208,118" +
  // Right upper arm (outer)
  " C212,128 216,142 218,156" +
  // Right forearm
  " C220,172 222,188 224,204 C226,218 228,232 228,240" +
  // Right hand
  " C228,248 230,256 232,260 C234,266 234,270 232,272" +
  " C230,274 226,272 224,268 C222,264 220,256 218,248" +
  // Right inner arm back up
  " C216,238 214,226 212,214 C210,200 208,186 206,172" +
  " C204,158 202,144 200,134" +
  // Right side of torso
  " C198,140 194,150 192,162" +
  " C190,176 188,192 186,206" +
  // Right waist (slight inward curve)
  " C184,216 182,224 180,230" +
  // Right hip (outward — wide)
  " C182,238 186,246 190,256" +
  // Right outer thigh (thick)
  " C192,270 192,286 190,302 C188,318 186,332 184,342" +
  // Right knee (outer — wide)
  " C183,352 182,360 182,368" +
  // Right outer shin (thick)
  " C183,382 184,396 183,412 C182,428 181,444 180,456" +
  // Right ankle + foot (big, clearly visible)
  " C179,462 177,468 176,474" +
  " C175,480 176,486 180,490 C186,496 194,500 204,502" +
  " C210,504 216,504 218,502 C220,500 220,496 216,494" +
  " C210,492 202,490 194,488 C186,486 180,484 178,480" +
  " C176,476 176,470 176,464 L178,460" +
  // Right inner shin back up
  " L166,456 C166,444 165,428 165,412" +
  " C165,396 165,382 165,368" +
  // Right inner knee
  " C167,360 167,352 166,342" +
  // Right inner thigh back up
  " C165,332 163,318 162,302 C160,286 159,270 158,256" +
  // Inner thigh to crotch
  " C157,250 156,246 155,242" +
  " L154,240 C153,238 152,236 150,236" +
  // Crotch (lower, wider gap)
  " C148,236 147,238 146,240 L145,242" +
  " C144,246 143,250 142,256" +
  // Left inner thigh
  " C141,270 140,286 138,302 C137,318 135,332 134,342" +
  // Left inner knee
  " C133,352 133,360 133,368" +
  // Left inner shin
  " C135,382 135,396 135,412" +
  " C135,428 134,444 134,456 L122,460" +
  // Left ankle + foot (big, clearly visible)
  " C121,464 124,470 124,474" +
  " C124,480 122,484 118,488 C112,490 104,492 96,494" +
  " C90,496 84,496 82,494 C80,492 80,490 84,488" +
  " C90,486 98,484 106,482 C114,480 120,478 122,474" +
  " C124,470 124,464 122,458 L120,456" +
  // Left outer shin (thick)
  " C119,444 118,428 117,412 C116,396 117,382 118,368" +
  // Left knee (outer — wide)
  " C118,360 117,352 116,342" +
  // Left outer thigh (thick)
  " C114,332 112,318 110,302 C108,286 108,270 110,256" +
  // Left hip (outward — wide)
  " C114,246 118,238 120,230" +
  // Left waist
  " C118,224 116,216 114,206" +
  " C112,192 110,176 108,162" +
  " C106,150 102,140 100,134" +
  // Left inner arm down
  " C98,144 96,158 94,172" +
  " C92,186 90,200 88,214 C86,226 84,238 82,248" +
  // Left hand
  " C80,256 78,264 76,268 C74,272 70,274 68,272" +
  " C66,270 66,266 68,260 C70,256 72,248 72,240" +
  // Left forearm back up
  " C72,232 74,218 76,204 C78,188 80,172 82,156" +
  // Left upper arm
  " C84,142 88,128 92,118" +
  // Left shoulder
  " C94,112 100,106 106,102 C116,96 128,92 136,88" +
  // Left side of neck
  " C140,86 142,82 142,78 L145,75" +
  // Left jaw up
  " C142,74 138,72 134,70 C128,66 122,58 122,54" +
  // Left ear
  " C122,56 120,56 119,54 C118,52 118,49 119,46 C120,44 122,42 122,38" +
  // Left side of head back to top
  " C122,20 132,8 150,8 Z";

const BODY_PARTS = [BODY_SILHOUETTE];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BodyMap({
  value: markers,
  onChange,
  isInjured = "na",
  onIsInjuredChange,
  conditionWorsened = "na",
  onConditionWorsenedChange,
}: BodyMapProps) {
  const [activeSide, setActiveSide] = useState<"front" | "back">("front");
  const [selectedType, setSelectedType] = useState<InjuryType>("bruise");
  const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const regions = activeSide === "front" ? FRONT_REGIONS : BACK_REGIONS;
  const sideMarkers = markers.filter((m) => m.side === activeSide);

  const getInjuryConfig = (type: InjuryType) =>
    INJURY_TYPES.find((t) => t.key === type)!;

  // Convert mouse/touch event to SVG coordinates
  const toSvgCoords = useCallback(
    (e: React.MouseEvent<SVGElement> | React.TouchEvent<SVGElement>) => {
      const svg = svgRef.current;
      if (!svg) return null;

      const pt = svg.createSVGPoint();
      const clientX =
        "touches" in e ? e.touches[0]!.clientX : (e as React.MouseEvent).clientX;
      const clientY =
        "touches" in e ? e.touches[0]!.clientY : (e as React.MouseEvent).clientY;

      pt.x = clientX;
      pt.y = clientY;
      const ctm = svg.getScreenCTM();
      if (!ctm) return null;
      const svgPt = pt.matrixTransform(ctm.inverse());
      return { x: Math.round(svgPt.x * 10) / 10, y: Math.round(svgPt.y * 10) / 10 };
    },
    [],
  );

  // Determine which named region a point falls in
  const findRegion = useCallback(
    (x: number, y: number): string => {
      // Use SVG hit testing via elementFromPoint would be ideal,
      // but since we have the regions, find which polygon contains the point
      // using a simple bounding-box approach for each region
      for (const region of regions) {
        // Parse path to get bounding box (simplified)
        const coords = region.path
          .match(/[\d.]+/g)
          ?.map(Number);
        if (!coords || coords.length < 4) continue;

        const xs: number[] = [];
        const ys: number[] = [];
        for (let i = 0; i < coords.length; i += 2) {
          xs.push(coords[i]!);
          ys.push(coords[i + 1]!);
        }

        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
          return region.label;
        }
      }
      return "Body";
    },
    [regions],
  );

  // Handle click on body to place marker
  const handleBodyClick = useCallback(
    (e: React.MouseEvent<SVGElement>) => {
      // Don't place a marker if we clicked on an existing marker
      const target = e.target as Element;
      if (target.closest("[data-marker]")) {
        return;
      }

      const coords = toSvgCoords(e);
      if (!coords) return;

      const region = findRegion(coords.x, coords.y);

      const newMarker: InjuryMarker = {
        id: crypto.randomUUID(),
        side: activeSide,
        x: coords.x,
        y: coords.y,
        region,
        injuryType: selectedType,
      };

      onChange([...markers, newMarker]);
      setSelectedMarker(null);
    },
    [activeSide, selectedType, markers, onChange, toSvgCoords, findRegion],
  );

  const handleMarkerClick = useCallback(
    (e: React.MouseEvent, markerId: string) => {
      e.stopPropagation();
      setSelectedMarker(selectedMarker === markerId ? null : markerId);
    },
    [selectedMarker],
  );

  const removeMarker = useCallback(
    (markerId: string) => {
      onChange(markers.filter((m) => m.id !== markerId));
      setSelectedMarker(null);
    },
    [markers, onChange],
  );

  const undoLast = useCallback(() => {
    if (markers.length > 0) {
      onChange(markers.slice(0, -1));
    }
  }, [markers, onChange]);

  const clearAll = useCallback(() => {
    onChange([]);
    setSelectedMarker(null);
  }, [onChange]);

  const frontCount = markers.filter((m) => m.side === "front").length;
  const backCount = markers.filter((m) => m.side === "back").length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Injury Documentation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Injured / Condition radio groups */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Injured?
            </label>
            <div className="flex items-center gap-4">
              {(
                [
                  ["yes", "Yes"],
                  ["no", "No"],
                  ["na", "N/A"],
                ] as const
              ).map(([val, label]) => (
                <label key={val} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="isInjured"
                    checked={isInjured === val}
                    onChange={() => onIsInjuredChange?.(val)}
                    className="h-4 w-4 accent-primary"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Condition Worsened?
            </label>
            <div className="flex items-center gap-4">
              {(
                [
                  ["yes", "Yes"],
                  ["no", "No"],
                  ["na", "N/A"],
                ] as const
              ).map(([val, label]) => (
                <label key={val} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="conditionWorsened"
                    checked={conditionWorsened === val}
                    onChange={() => onConditionWorsenedChange?.(val)}
                    className="h-4 w-4 accent-primary"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Injury type selector toolbar */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Select Injury Type, Then Click on Body
          </label>
          <div className="flex flex-wrap gap-2">
            {INJURY_TYPES.map((type) => (
              <button
                key={type.key}
                type="button"
                onClick={() => setSelectedType(type.key)}
                className={cn(
                  "flex items-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all",
                  selectedType === type.key
                    ? "border-foreground bg-foreground text-background shadow-md scale-105"
                    : "border-border bg-background text-foreground hover:border-foreground/50 hover:shadow-sm",
                )}
              >
                <span
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white",
                    type.bgColor,
                  )}
                >
                  {type.letter}
                </span>
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Front/Back toggle + actions */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center rounded-lg border bg-muted p-1">
            <button
              type="button"
              onClick={() => setActiveSide("front")}
              className={cn(
                "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                activeSide === "front"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Front {frontCount > 0 && `(${frontCount})`}
            </button>
            <button
              type="button"
              onClick={() => setActiveSide("back")}
              className={cn(
                "rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                activeSide === "back"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Back {backCount > 0 && `(${backCount})`}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={undoLast}
              disabled={markers.length === 0}
              className="gap-1.5 text-xs"
            >
              <Undo2 className="h-3.5 w-3.5" />
              Undo
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearAll}
              disabled={markers.length === 0}
              className="gap-1.5 text-xs text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Clear All
            </Button>
          </div>
        </div>

        {/* Body diagram */}
        <div className="flex justify-center">
          <div className="relative w-full max-w-sm">
            {/* Side label */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 rounded-full bg-muted px-3 py-0.5 text-xs font-medium text-muted-foreground z-10">
              {activeSide === "front" ? "Front View" : "Back View"}
            </div>

            <svg
              ref={svgRef}
              viewBox="0 0 300 516"
              className="w-full cursor-crosshair"
              onClick={handleBodyClick}
            >
              {/* Background */}
              <rect
                x="0"
                y="0"
                width="300"
                height="516"
                fill="transparent"
              />

              {/* Clickable body regions (invisible, for hover detection) */}
              {regions.map((region) => (
                <path
                  key={region.name}
                  d={region.path}
                  fill={
                    hoveredRegion === region.name
                      ? "rgba(59, 130, 246, 0.15)"
                      : "transparent"
                  }
                  stroke="none"
                  onMouseEnter={() => setHoveredRegion(region.name)}
                  onMouseLeave={() => setHoveredRegion(null)}
                  className="transition-colors"
                />
              ))}

              {/* Body outline (multi-part realistic silhouette) */}
              {BODY_PARTS.map((d, i) => (
                <path
                  key={i}
                  d={d}
                  fill="rgba(200,180,160,0.12)"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  className="text-foreground/40"
                  pointerEvents="none"
                />
              ))}

              {/* Center line for orientation */}
              <line
                x1="150"
                y1="78"
                x2="150"
                y2="258"
                stroke="currentColor"
                strokeWidth="0.5"
                strokeDasharray="4 4"
                className="text-foreground/10"
                pointerEvents="none"
              />

              {/* Region labels on hover */}
              {hoveredRegion && (() => {
                const region = regions.find((r) => r.name === hoveredRegion);
                if (!region) return null;
                const coords = region.path.match(/[\d.]+/g)?.map(Number);
                if (!coords || coords.length < 4) return null;
                const xs: number[] = [];
                const ys: number[] = [];
                for (let i = 0; i < coords.length; i += 2) {
                  xs.push(coords[i]!);
                  ys.push(coords[i + 1]!);
                }
                const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
                const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
                return (
                  <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="8"
                    fill="currentColor"
                    className="text-foreground/40 pointer-events-none select-none"
                    pointerEvents="none"
                  >
                    {region.label}
                  </text>
                );
              })()}

              {/* Injury markers */}
              {sideMarkers.map((marker) => {
                const config = getInjuryConfig(marker.injuryType);
                const isSelected = selectedMarker === marker.id;
                return (
                  <g
                    key={marker.id}
                    data-marker
                    onClick={(e) => handleMarkerClick(e, marker.id)}
                    className="cursor-pointer"
                  >
                    {/* Selection ring */}
                    {isSelected && (
                      <circle
                        cx={marker.x}
                        cy={marker.y}
                        r="14"
                        fill="none"
                        stroke={config.color}
                        strokeWidth="2"
                        strokeDasharray="3 2"
                        className="animate-pulse"
                      />
                    )}
                    {/* Marker circle */}
                    <circle
                      cx={marker.x}
                      cy={marker.y}
                      r="10"
                      fill={config.color}
                      stroke="white"
                      strokeWidth="2"
                      opacity={0.9}
                    />
                    {/* Injury type letter */}
                    <text
                      x={marker.x}
                      y={marker.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize="10"
                      fontWeight="bold"
                      fill="white"
                      pointerEvents="none"
                    >
                      {config.letter}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Click instruction */}
            {sideMarkers.length === 0 && (
              <p className="text-center text-xs text-muted-foreground mt-1">
                Click on the body to place an injury marker
              </p>
            )}
          </div>
        </div>

        {/* Selected marker actions */}
        {selectedMarker && (() => {
          const marker = markers.find((m) => m.id === selectedMarker);
          if (!marker) return null;
          const config = getInjuryConfig(marker.injuryType);
          return (
            <div className="flex items-center justify-between rounded-lg border bg-muted/50 px-4 py-3">
              <div className="flex items-center gap-3">
                <span
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold text-white",
                    config.bgColor,
                  )}
                >
                  {config.letter}
                </span>
                <div>
                  <p className="text-sm font-medium">{config.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {marker.region} ({marker.side})
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeMarker(marker.id)}
                className="gap-1.5"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </Button>
            </div>
          );
        })()}

        {/* Injury summary list */}
        {markers.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">
              Injuries Documented ({markers.length})
            </h4>
            <div className="divide-y rounded-lg border">
              {markers.map((marker, idx) => {
                const config = getInjuryConfig(marker.injuryType);
                return (
                  <div
                    key={marker.id}
                    className={cn(
                      "flex items-center justify-between px-3 py-2 text-sm",
                      selectedMarker === marker.id && "bg-accent",
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs text-muted-foreground w-5">
                        {idx + 1}.
                      </span>
                      <span
                        className={cn(
                          "flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white",
                          config.bgColor,
                        )}
                      >
                        {config.letter}
                      </span>
                      <span className="font-medium">{config.label}</span>
                      <span className="text-muted-foreground">
                        - {marker.region}
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">
                        ({marker.side})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMarker(marker.id)}
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
